import { useEffect, useRef, useState } from "react";

import { fetchSignedFile, saveBlob } from "../../api/download";
import { useGetFirstAdDelivery } from "../../api/generated/endpoints/generation/generation";
import { soonest, urlsDue } from "../../api/signed-urls";
import { FALLBACK_REFETCH_MS, canChange, jobPageInterval } from "../../platform/jobs/refetch";
import { useStreamStatus } from "../../platform/stream-status";

export interface FirstAdDeliveryOptions {
  teamId: string;
  brandId: string;
  /** Only the run returned by the saved first-ad state; the server rechecks ownership. */
  runId: string | null;
  enabled?: boolean;
}

/** c2 owns display/composition; this hook only reads and downloads protected delivery. */
export function useFirstAdDelivery({ teamId, brandId, runId, enabled = true }: FirstAdDeliveryOptions) {
  const stream = useStreamStatus();
  const active = enabled && runId !== null;
  const scopeKey = JSON.stringify([teamId, brandId, runId, active]);
  const pending = useRef<AbortController | null>(null);
  const [downloadState, setDownloadState] = useState<{ key: string; busy: boolean; error: unknown }>({
    key: scopeKey, busy: false, error: null,
  });
  const query = useGetFirstAdDelivery(teamId, brandId, runId ?? "", {
    query: {
      enabled: active,
      refetchInterval: (read) => active ? soonest(
        jobPageInterval(read.state.data ? { status: read.state.data.status, urls_expire_at: read.state.data.urls_expire_at ?? null } : undefined, stream),
        read.state.data?.previews_pending && stream !== "live" ? FALLBACK_REFETCH_MS : false,
      ) : false,
      refetchOnWindowFocus: (read) => active && (canChange(read.state.data?.status) || read.state.data?.previews_pending
        || urlsDue(read.state.data?.urls_expire_at)) ? "always" : false,
    },
  });
  useEffect(() => () => { pending.current?.abort(); pending.current = null; }, [scopeKey]);

  const download = async () => {
    if (!active || pending.current !== null) return;
    const controller = new AbortController();
    pending.current = controller;
    setDownloadState({ key: scopeKey, busy: true, error: null });
    try {
      // Recheck authorization and retention; never fall back to the cached URL.
      const fresh = await query.refetch({ throwOnError: true });
      if (controller.signal.aborted) return;
      if (!fresh.data?.complete || !fresh.data.download_url) {
        throw new Error(fresh.data?.problem ?? "Your protected video is not ready to download.");
      }
      const file = await fetchSignedFile(fresh.data.download_url, controller.signal);
      if (!controller.signal.aborted) saveBlob(file, "created-ai-first-ad-watermarked.mp4");
    } catch (error) {
      if (!controller.signal.aborted) setDownloadState({ key: scopeKey, busy: false, error });
    } finally {
      if (pending.current === controller) {
        pending.current = null;
        setDownloadState((current) => ({ ...current, busy: false }));
      }
    }
  };
  return {
    ...query,
    // A failed authorization refresh must not leave a stale playable URL in c2.
    data: active && !query.isError ? query.data : undefined,
    download,
    downloading: downloadState.key === scopeKey && downloadState.busy,
    downloadError: downloadState.key === scopeKey ? downloadState.error : null,
  };
}
